import { BaseApiService } from './base.service';
import type { JsonApiResponse } from './base.service';
import type {
  Render,
  RenderCreateRequest,
  RenderCreateResponse,
} from '@/types/render';
import type { ProjectReference } from '@/types/project-reference';

export class RendersService extends BaseApiService {
  async createRender(
    conceptId: string,
    data: RenderCreateRequest,
    conceptAngleId: string
  ): Promise<Render> {
    const url = `/v1/concepts/${conceptId}/angles/${conceptAngleId}/renders`;
    const response = await this.post<RenderCreateResponse>(
      url,
      data as unknown as Record<string, unknown>,
      undefined,
      {
        loading: { trackEndpoint: 'render.create' },
      }
    );
    return response.data;
  }

  async getRender(renderId: string): Promise<Render> {
    const response = await this.httpGet<JsonApiResponse<Render>>(
      `/v1/renders/${renderId}`,
      undefined,
      {
        loading: { trackEndpoint: `render.${renderId}` },
      }
    );
    return response.data;
  }

  async updateRender(
    renderId: string,
    updates: Partial<Render['attributes']>
  ): Promise<Render> {
    const response = await this.patch<JsonApiResponse<Render>>(
      `/v1/renders/${renderId}`,
      { data: { type: 'render', id: renderId, attributes: updates } },
      undefined,
      {
        loading: { trackEndpoint: 'render.update' },
      }
    );
    return response.data;
  }

  async deleteRender(renderId: string): Promise<void> {
    await this.httpDelete(`/v1/renders/${renderId}`, undefined, {
      loading: { trackEndpoint: 'render.delete' },
    });
  }

  async cancelRender(
    renderId: string
  ): Promise<{ cancelled: boolean; message: string }> {
    const response = await this.httpDelete<{
      data: { attributes: { cancelled: boolean; message: string } };
    }>(`/v1/renders/${renderId}/cancel`);
    return (
      response.data.attributes || {
        cancelled: true,
        message: 'Render cancelled successfully',
      }
    );
  }

  async restoreRender(renderId: string): Promise<void> {
    await this.post(`/v1/renders/${renderId}/restore`);
  }

  async upscaleRender(renderId: string): Promise<{ data: Render }> {
    const response = await this.post<JsonApiResponse<Render>>(
      `/v1/renders/${renderId}/upscale`,
      undefined,
      undefined,
      {
        loading: { trackEndpoint: 'render.upscale' },
      }
    );
    return { data: response.data };
  }

  async saveRenderAsReference(renderId: string): Promise<ProjectReference> {
    const response = await this.post<JsonApiResponse<ProjectReference>>(
      `/v1/renders/${renderId}/save-as-reference`,
      undefined,
      undefined,
      {
        loading: { trackEndpoint: 'render.saveAsReference' },
      }
    );
    return response.data;
  }

  /**
   * Signed download link for a render.
   *
   * With `branded` the link points at a copy carrying the user's own marks
   * (logo / company line / AI-visualization label) instead of the stored
   * master. The master is never modified, so a plain download stays unmarked
   * and a logo change applies to designs generated before it was uploaded.
   * Safe to request unconditionally — with nothing configured the server falls
   * back to the master and reports `is_branded: false`.
   */
  async getDownloadUrl(renderId: string, branded = false): Promise<{
    url: string;
    filename: string;
    expires_in: number;
    is_branded?: boolean;
  }> {
    const response = await this.httpGet<{
      data: {
        type: 'download-url';
        id: string;
        attributes: {
          url: string;
          filename: string;
          expires_in: number;
          is_branded?: boolean;
        };
      };
    }>(`/v1/renders/${renderId}/download${branded ? '?branded=1' : ''}`, undefined, {
      loading: { trackEndpoint: 'render.download' },
    });
    return response.data.attributes;
  }
}

export const rendersService = new RendersService();
